import { useMemo } from 'react'

import { type QuotingQuoteResult } from 'utils/web_api.schemas'

import CostImpactBarChart from './CostImpactBarChart'
import CostImpactSpectrum from './CostImpactSpectrum'
import { type BucketData, type EmployeeWithCostData, type SpectrumData } from './types'
import { BUCKET_COUNT, createBuckets, DARK_GREEN_COLOR, DARK_RED_COLOR, getEmployeeCostData } from './utils'

export default function EmployeeCostImpact ({
  employees,
  employerEmployeeContribution,
  employerSpouseContribution,
  employerChildContribution,
  isLoading,
  onBucketChange,
}: {
  employees: QuotingQuoteResult[]
  employerEmployeeContribution: number
  employerSpouseContribution: number
  employerChildContribution: number
  isLoading: boolean
  onBucketChange?: (bucket: BucketData | null) => void
}) {
  const handleBucketClick = (bucket: BucketData) => {
    if (onBucketChange) {
      onBucketChange(bucket)
    }
  }

  const { buckets, hasData, spectrumData } = useMemo(() => {
    const costData = employees
      .map(emp => getEmployeeCostData(
        emp,
        employerEmployeeContribution,
        employerSpouseContribution,
        employerChildContribution
      ))
      .filter((data): data is EmployeeWithCostData => data !== null)

    if (costData.length === 0) {
      return { buckets: [], hasData: false, spectrumData: [] }
    }

    const calculatedBuckets = createBuckets(costData, BUCKET_COUNT)

    // Calculate spectrum chart data: employees paying more vs saving
    const payingMoreEmployees = costData.filter(e => e.difference < 0)
    const savingEmployees = costData.filter(e => e.difference >= 0)
    const total = costData.length

    // Find extreme employees (most paying more / most saving), including ties
    const minDifference = payingMoreEmployees.length > 0
      ? Math.min(...payingMoreEmployees.map(e => e.difference))
      : null
    const maxDifference = savingEmployees.length > 0
      ? Math.max(...savingEmployees.map(e => e.difference))
      : null

    const mostPayingMore = minDifference !== null
      ? payingMoreEmployees.filter(e => e.difference === minDifference)
      : []
    const mostSaving = maxDifference !== null
      ? savingEmployees.filter(e => e.difference === maxDifference)
      : []

    const spectrumData: SpectrumData[] = [
      {
        name: 'Paying More',
        value: payingMoreEmployees.length,
        percentage: total > 0 ? Math.round((payingMoreEmployees.length / total) * 100) : 0,
        extremeEmployees: mostPayingMore.length > 0
          ? { employees: mostPayingMore.map(e => e.employee.name), amount: minDifference! }
          : undefined,
      },
      {
        name: 'Paying Less',
        value: savingEmployees.length,
        percentage: total > 0 ? Math.round((savingEmployees.length / total) * 100) : 0,
        extremeEmployees: mostSaving.length > 0
          ? { employees: mostSaving.map(e => e.employee.name), amount: maxDifference! }
          : undefined,
      },
    ]

    return {
      buckets: calculatedBuckets,
      hasData: true,
      spectrumData,
    }
  }, [employees, employerEmployeeContribution, employerSpouseContribution, employerChildContribution])

  if (isLoading) {
    return (
      <div className='mt-8'>
        <h2 className='text-lg font-semibold mb-4'>Employee Cost Impact</h2>
        <div className='h-64 bg-gray-100 animate-pulse rounded-lg' />
      </div>
    )
  }

  if (!hasData) {
    return (
      <div className='mt-8'>
        <h2 className='text-lg font-semibold mb-4'>Employee Cost Impact</h2>
        <p className='text-gray-500'>
          No employee cost data available. If you would like the <span className='italic'>Employee Cost Impact</span> visual, please generate a new quote with the <span className='italic'>employer_medical_contribution</span> and <span className='italic'>employee_medical_contribution</span> fields present in the census file.
        </p>
      </div>
    )
  }

  return (
    <div className='mt-8'>
      <h2 className='text-lg font-semibold mb-2'>Employee Cost Impact</h2>
      <div className='flex items-start gap-4 mb-4' style={{ overflow: 'visible' }}>
        <p className='text-sm text-gray-500 flex-1'>
          Distribution of the employee portion of the monthly premium compared to their portion of their current plan.
          <span className='font-medium' style={{ color: DARK_GREEN_COLOR }}> Green</span> indicates savings,
          <span className='font-medium' style={{ color: DARK_RED_COLOR }}> red</span> indicates increased costs.
        </p>
      </div>

      <CostImpactBarChart buckets={buckets} onBucketClick={handleBucketClick} />
      <CostImpactSpectrum data={spectrumData} />
    </div>
  )
}
